import type React from 'react';
import { useState } from 'react';
import {
  Button,
  Modalv2,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputSelect,
  Label,
  FormGroup,
  FormControl
} from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import type { DealSearchCondition } from '~/models/Deal';
import { TYPE_OPTIONS, ENCODING_OPTIONS } from '~/models/CsvGenerationResult';
import type {
  CsvRequestParams,
  Type,
  Encoding
} from '~/models/CsvGenerationResult';
import type { NskResponse } from '~/models/Api';

export type DealsCsvExportModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  searchParams: DealSearchCondition;
  requestCsvGenerate: (
    params: DealSearchCondition,
    csvType: Type,
    encoding: Encoding
  ) => Promise<NskResponse<CsvRequestParams>>;
};

const DealsCsvExportModal: React.FC<DealsCsvExportModalProps> = ({
  searchParams,
  requestCsvGenerate,
  handleClose,
  isOpen
}) => {
  const [csvType, setCsvType] = useState<Type>(TYPE_OPTIONS[0].value);
  const [encoding, setEncoding] = useState<Encoding>(ENCODING_OPTIONS[0].value);
  const [loading, setLoading] = useState(false);

  const handleDownload = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    const response = await requestCsvGenerate(searchParams, csvType, encoding);
    if (response.type === 'success') {
      toast.success(
        'CSV出力リクエストを送信しました。作成後、メールで通知されます。'
      );
      handleClose();
    } else {
      const msg = response.message
        ? `CSV出力リクエストに失敗しました。${response.message}`
        : 'CSV出力リクエストに失敗しました';
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <Modalv2 isOpen={isOpen} onClose={handleClose} size="s">
      <ModalHeader title="CSV出力" />
      <ModalBody>
        <FormGroup variant="vertical">
          <FormControl chip="必須" label="出力種別">
            <InputSelect
              onChange={(e): void => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const val = e.target.value as Type;
                setCsvType(val);
              }}
              value={csvType}
              w="100%"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </InputSelect>
          </FormControl>
          <FormControl chip="必須" label="文字コード">
            <InputSelect
              onChange={(e): void => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const val = e.target.value as Encoding;
                setEncoding(val);
              }}
              value={encoding}
              w="100%"
            >
              {ENCODING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </InputSelect>
          </FormControl>
          <Label color="subtle" size="s">
            ※CSVの出力には時間がかかることがあります。作成後、メールで通知されます。
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter
        leftArea={
          <Button onClick={handleClose} variant="secondary">
            キャンセル
          </Button>
        }
        rightArea={
          <Button
            loading={loading}
            onClick={handleDownload}
            type="button"
            variant="primary"
          >
            出力
          </Button>
        }
      />
    </Modalv2>
  );
};

export default DealsCsvExportModal;
